import React, { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { Button, Skeleton, CircularProgress } from "@ann/web-components";
import { RolesContainer, RolesGridWrapper, LoadMoreButtonContainer, LoadingContainer } from "./Roles.styled";
import { EmptyStateRoles, RoleCard, RolesFilters } from "./components";
import { AssignRoleModal } from "../../../../components";
import { useBackendPagination } from "../../../../hooks/useBackendPagination";
import { getRoleList } from "../../../../services";
import { useToast } from "../../../../../../../components/toast";

// Map sort option to API field name
const SORT_FIELD_MAP = {
  createdAt: "created_at",
  name: "name",
  updatedAt: "updated_at",
};

const mapSortOptionToField = (option) => {
  return SORT_FIELD_MAP[option] || "created_at";
};

const Roles = ({ breadcrumbHelpers }) => {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("createdAt");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const searchTimeoutRef = useRef(null);
  const resetRef = useRef(null);
  const isFirstRenderRef = useRef(true);
  const sortOptionRef = useRef(sortOption);
  const orderDirectionRef = useRef(orderDirection);

  useEffect(() => {
    breadcrumbHelpers?.setPageBreadcrumb?.("Roles", null, { pageHref: "/v2/user-management/roles" });
  }, [breadcrumbHelpers]);

  // Configure fetch function for getRoleList - memoized to avoid recreating on each render
  const fetchRoles = useCallback(async (params) => {
    try {
      const response = await getRoleList(params);
      // Transform API response to match useBackendPagination expected format
      const statusCode = response.status_code || response.statusCode || 200;
      const responseBody = response.body || [];
      const meta = response.meta || {};

      // Normalize meta to ensure has_next is always present (support both has_next and hasNext)
      const normalizedMeta = {
        ...meta,
        has_next: meta.has_next !== undefined ? meta.has_next : (meta.hasNext || false)
      };

      if (statusCode >= 200 && statusCode < 300) {
        return {
          items: Array.isArray(responseBody) ? responseBody : [],
          meta: normalizedMeta
        };
      } else {
        throw new Error(response.msg_code?.[0]?.description || 'Failed to fetch roles');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Items per page constant - must match itemsPerPage in hook options
  const ITEMS_PER_PAGE = 6;

  // Build search params based on searchTerm
  const buildSearchParams = useCallback((keyword) => {
    if (keyword && keyword.trim()) {
      return { search: { keyword: keyword.trim() } };
    }
    return { search: undefined };
  }, []);

  // Use backend pagination hook
  const {
    items: allRoles,
    isLoading,
    error,
    hasMore,
    loadMore,
    reset,
    meta,
  } = useBackendPagination(
    fetchRoles,
    {
      order_by: [mapSortOptionToField(sortOption)],
      order_direction: [orderDirection.toUpperCase()]
    },
    {
      itemsPerPage: ITEMS_PER_PAGE,
      autoLoadFirstPage: true,
      onError: (error) => {
        const errorMessage = error?.message || 'Error loading roles. Please try again later.';
        toast.error({ 
          message: errorMessage, 
          duration: 4000, 
          canClose: true 
        });
      }
    }
  );

  // Store reset function in ref for use in debounced search
  useEffect(() => {
    resetRef.current = reset;
  }, [reset]);

  // Keep refs in sync with state
  useEffect(() => {
    sortOptionRef.current = sortOption;
  }, [sortOption]);

  useEffect(() => {
    orderDirectionRef.current = orderDirection;
  }, [orderDirection]);

  // Debounced search effect - only for search term changes
  useEffect(() => {
    // Skip first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const searchParams = buildSearchParams(searchTerm);
      resetRef.current?.({
        ...searchParams,
        order_by: [mapSortOptionToField(sortOptionRef.current)],
        order_direction: [orderDirectionRef.current.toUpperCase()]
      });
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, buildSearchParams]);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  // Helper function to build and execute reset
  const executeReset = useCallback((newSortOption, newOrderDirection) => {
    const searchParams = buildSearchParams(searchTerm);
    resetRef.current?.({
      ...searchParams,
      order_by: [mapSortOptionToField(newSortOption)],
      order_direction: [newOrderDirection.toUpperCase()]
    });
  }, [buildSearchParams, searchTerm]);

  const handleSortChange = useCallback((value) => {
    setSortOption(value);
    // Immediate reset for sort change
    executeReset(value, orderDirection);
  }, [executeReset, orderDirection]);

  const handleOrderChange = useCallback((currentOrder) => {
    // Toggle order: asc -> desc, desc -> asc
    const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
    setOrderDirection(newOrder);
    // Immediate reset for order change
    executeReset(sortOption, newOrder);
  }, [executeReset, sortOption]);

  // Transform API roles to card format (keeping all original data)
  const listRolesCards = useMemo(() => {
    return allRoles.map((role) => ({
      id: role.roleUuid || role.id,
      title: role.name || role.title,
      description: role.description || "",
      // Keep all original role data for navigation
      roleData: role
    }));
  }, [allRoles]);

  // Calculate hasMore based on meta or by comparing items vs total
  const calculatedHasMore = useMemo(() => {
    if (meta) {
      // Check both snake_case and camelCase
      if (meta.has_next !== undefined) return meta.has_next;
      if (meta.hasNext !== undefined) return meta.hasNext;
      
      // Fallback: calculate based on total_items
      if (meta.total_items !== undefined && meta.total_items > 0) {
        return allRoles.length < meta.total_items;
      }
    }
    return hasMore;
  }, [meta, allRoles.length, hasMore]);

  const handleCreateRole = () => {
    // TODO: Implement create role functionality
    console.log("Create role clicked");
  };

  const handleRoleClick = (roleId) => {
    // TODO: Implement role click functionality (e.g., navigate to role details)
  };

  const handleAssignRoleClick = useCallback((role) => {
    setSelectedRole(role);
    setIsAssignModalOpen(true);
  }, []);

  const handleAssignModalClose = useCallback(() => {
    setIsAssignModalOpen(false);
    setSelectedRole(null);
  }, []);

  const handleLoadMore = async () => {
    try {
      setIsLoadingMore(true);
      await loadMore();
    } catch (error) {
      // Error already handled in onError callback
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Reusable loading spinner
  const loadingSpinner = (
    <LoadingContainer>
      <CircularProgress size="xl" />
    </LoadingContainer>
  );

  // Show empty state when there are no roles and not loading
  if (!isLoading && listRolesCards.length === 0) {
    return (
      <RolesContainer>
        {/* Show filters when searching so user can clear the search */}
        {searchTerm && (
          <RolesFilters
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onOrderChange={handleOrderChange}
            selectedSortOption={sortOption}
            orderDirection={orderDirection}
            loading={isLoading}
          />
        )}
        <EmptyStateRoles onAction={handleCreateRole} />
      </RolesContainer>
    );
  }

  return (
    <RolesContainer>
      <RolesFilters
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={sortOption}
        orderDirection={orderDirection}
        loading={isLoading}
      />
      {isLoading && listRolesCards.length === 0 ? (
        loadingSpinner
      ) : (
        <>
          {/* Show CircularProgress overlay when filtering/sorting (not Load More) */}
          {isLoading && !isLoadingMore && listRolesCards.length > 0 && loadingSpinner}

          {/* Hide cards when filtering/sorting, show when loading more */}
          {(!isLoading || isLoadingMore) && (
            <RolesGridWrapper>
              {listRolesCards.map((role) => (
                <RoleCard
                  key={role.id}
                  id={role.id}
                  title={role.title}
                  description={role.description}
                  roleData={role.roleData}
                  onClick={handleRoleClick}
                  onAssignClick={handleAssignRoleClick}
                />
              ))}
              
              {/* Show skeletons only when loading more items (after clicking Load More) */}
              {isLoadingMore && (
                <>
                  {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                    <div key={`loading-skeleton-${i}`}>
                      <Skeleton height="93px" width="100%" />
                    </div>
                  ))}
                </>
              )}
            </RolesGridWrapper>
          )}

          {!isLoading && calculatedHasMore && (
            <LoadMoreButtonContainer>
              <Button
                size="medium"
                type="tertiary"
                onClick={handleLoadMore}
                label="Load More"
                disableEffects={true}
              />
            </LoadMoreButtonContainer>
          )}
        </>
      )}
      <AssignRoleModal
        isOpen={isAssignModalOpen}
        onClose={handleAssignModalClose}
        roleUuid={selectedRole?.id || ""}
        roleName={selectedRole?.title || ""}
        onAssign={(result) => {
          if (result.success) {
            // Optionally refresh the list or show success state
            console.log("Role assigned successfully:", result);
          }
        }}
      />
    </RolesContainer>
  );
};

export default Roles;

